import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { 
  Truck, 
  Search, 
  MapPin, 
  Shield, 
  Zap, 
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Users,
  Package,
  Clock
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Search,
      titleKey: 'landing.features.smartmatch.title',
      descKey: 'landing.features.smartmatch.desc',
      color: 'bg-blue-500'
    },
    {
      icon: MapPin,
      titleKey: 'landing.features.tracking.title',
      descKey: 'landing.features.tracking.desc',
      color: 'bg-emerald-500'
    },
    {
      icon: Shield,
      titleKey: 'landing.features.verified.title',
      descKey: 'landing.features.verified.desc',
      color: 'bg-purple-500'
    },
    {
      icon: Zap,
      titleKey: 'landing.features.simple.title',
      descKey: 'landing.features.simple.desc',
      color: 'bg-orange-500'
    }
  ];

  const stats = [
    { value: '500+', labelKey: 'landing.stats.carriers', icon: Truck },
    { value: '2000+', labelKey: 'landing.stats.missions', icon: Package },
    { value: '98%', labelKey: 'landing.stats.satisfaction', icon: TrendingUp },
    { value: '<5min', labelKey: 'landing.stats.matching', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl font-['Chivo'] tracking-tight text-slate-900">{t('common.appName')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/login">
                <Button variant="ghost" className="hidden md:inline-flex" data-testid="login-nav-btn">
                  {t('auth.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="register-nav-btn">
                  {t('auth.register')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1696583545337-05099b905626?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-orange-500/20 text-orange-300 border-orange-500/30 px-4 py-1.5">
              {t('landing.hero.badge')}
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white font-['Chivo'] tracking-tight leading-none mb-6">
              {t('landing.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg" data-testid="hero-cta-btn">
                  {t('auth.register')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  {t('auth.login')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur-lg border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white font-['JetBrains_Mono']">{stat.value}</div>
                    <div className="text-sm text-slate-400">{t(stat.labelKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-slate-900 text-white">Fonctionnalités</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Chivo'] tracking-tight mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 font-['Chivo'] mb-2">{t(feature.titleKey)}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Enterprises Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">{t('auth.roleEnterprise')}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Chivo'] tracking-tight mb-6">
                Trouvez la capacité dont vous avez besoin, instantanément
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Accédez à un réseau de transporteurs vérifiés et disponibles. Notre système SmartMatch 
                vous propose les meilleurs candidats en quelques secondes.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Matching intelligent basé sur vos critères',
                  'Transporteurs certifiés et vérifiés',
                  'Suivi en temps réel de vos missions',
                  'Gestion simplifiée des contrats'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="enterprise-cta-btn">
                  Créer un compte Entreprise
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/4484078/pexels-photo-4484078.jpeg"
                  alt="Warehouse worker with tablet"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900 font-['JetBrains_Mono']">+30%</div>
                  <div className="text-sm text-slate-500">Efficacité</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Carriers Section */}
      <section className="py-24 md:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1761307234324-0c9eadb951de?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Shipping terminal"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -right-6 bg-orange-500 rounded-xl shadow-lg p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-white" />
                <div>
                  <div className="text-2xl font-bold text-white font-['JetBrains_Mono']">500+</div>
                  <div className="text-sm text-orange-100">Partenaires</div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="mb-4 bg-orange-500/20 text-orange-300 border-orange-500/30">{t('auth.roleCarrier')}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Chivo'] tracking-tight mb-6">
                Maximisez l&apos;utilisation de votre flotte
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Recevez des opportunités de missions adaptées à votre flotte. Plus de trajets à vide, 
                plus de revenus, moins d&apos;administratif.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Opportunités personnalisées selon votre flotte',
                  'Système de bidding simple et rapide',
                  'Paiements garantis et transparents',
                  'Accès aux grands donneurs d\'ordre'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="carrier-cta-btn">
                  Rejoindre le réseau
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-xl font-['Chivo'] tracking-tight text-white">{t('common.appName')}</span>
              </div>
              <p className="text-slate-400 text-sm">
                L&apos;écosystème intelligent de la sous-traitance logistique française et européenne.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Plateforme</h4>
              <ul className="space-y-2">
                <li><Link to="/register" className="text-slate-400 hover:text-white text-sm">{t('auth.register')}</Link></li>
                <li><Link to="/login" className="text-slate-400 hover:text-white text-sm">{t('auth.login')}</Link></li>
                <li><Link to="/pricing" className="text-slate-400 hover:text-white text-sm">{t('nav.pricing')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Ressources</h4>
              <ul className="space-y-2">
                <li><Link to="/compliance" className="text-slate-400 hover:text-white text-sm">{t('nav.compliance')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-slate-400 text-sm">
                © 2025 {t('common.appName')}. {t('legal.allRights')}.
              </p>
              <p className="text-slate-500 text-xs text-center">
                {t('legal.license')} <span className="text-orange-400 font-medium">{t('legal.licensee')}</span> — {t('legal.licenseeAddress')}
              </p>
            </div>
            <p className="text-slate-600 text-xs mt-2 text-center">
              {t('pricing.commission')}: 6% {t('pricing.enterprise')} • 2.5% {t('pricing.carrier')} — {t('legal.domain')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
